/**
 * Table — the mockup's table treatment, as small composable parts rather than a
 * config-driven grid. The panel's ten table pages differ too much in what a cell
 * contains for a `columns=[…]` API to fit without a prop per exception.
 *
 *   <Table>
 *     <THead><TR><TH>Domain</TH><TH className="w-10" /></TR></THead>
 *     <TBody>
 *       <TR><TD name>webanceapp.com</TD><TD mono>8.3</TD></TR>
 *     </TBody>
 *   </Table>
 *
 * Put it directly inside a <Card> — Card clips to the 8px radius, and Table
 * brings its own horizontal scroll container so a wide table scrolls inside the
 * card instead of pushing the page sideways.
 *
 * Deliberately NOT here yet (§7a: don't build beyond what the pages render):
 *  - Sort affordance. The spec mentions one, but no page sorts today and a ↓
 *    glyph that does nothing is a dead control. Add it with the first page that
 *    actually sorts.
 *  - Sticky header. Nothing scrolls a table in its own box yet.
 */

export function Table({ children, className = '' }) {
  return (
    <div className="overflow-x-auto">
      <table className={['w-full border-collapse', className].join(' ')}>
        {children}
      </table>
    </div>
  );
}

export function THead({ children }) {
  return <thead>{children}</thead>;
}

export function TBody({ children }) {
  return <tbody>{children}</tbody>;
}

/**
 * `hover` defaults on for body rows and is off in a THead. Rows lose their
 * bottom border on the last child so the table ends flush inside the card.
 */
export function TR({ children, hover = true, className = '', ...rest }) {
  return (
    <tr
      className={[
        hover ? 'hover:bg-s-2' : '',
        '[&>td]:border-b [&>td]:border-bd last:[&>td]:border-b-0',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </tr>
  );
}

export function TH({ children, className = '', ...rest }) {
  return (
    <th
      className={[
        'px-3.5 py-[11px] text-left align-middle whitespace-nowrap',
        'text-[11.5px] font-semibold uppercase tracking-[0.04em] text-t3',
        'border-b border-bd bg-s-1',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </th>
  );
}

/**
 * `name` = the row's identifying cell (heavier, --color-t1).
 * `mono` = a machine value (§2: IPs, hostnames, sizes, PHP versions, ids).
 */
export function TD({ children, name = false, mono = false, className = '', ...rest }) {
  return (
    <td
      className={[
        'px-3.5 py-[13px] align-middle',
        // Emit exactly one font-size utility. Listing both `text-[13.5px]` and
        // `text-[12.5px]` lets CSS source order decide the winner, not the order
        // in this array — which silently rendered mono cells at 13.5px.
        mono ? 'font-mono text-[12.5px]' : 'text-[13.5px]',
        name ? 'font-medium text-t1' : 'text-t2',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </td>
  );
}

/** Placeholder for an absent value. Never the sole carrier of meaning (§8). */
export function Empty() {
  return <span className="text-ic-light">—</span>;
}
